// Logic shared between search.ts (desktop/tablet header overlay, ≥700px)
// and search-page.ts (the dedicated mobile /search/ page). Both drive an
// equivalent ".search-shell" root (see search.scss) — the "recently
// viewed" list, the locale-filter seeding, and the mobile filter-pane fade
// behave identically regardless of which shell hosts them, so this is
// genuinely one component's logic reused in two host contexts, not two
// unrelated components coincidentally sharing code.

export interface RecentItem {
    title: string;
    url: string;
}

const RECENT_STORAGE_KEY = "gs-recently-viewed";
const RECENT_MAX = 5;
const LOCALE_SEED_TIMEOUT_MS = 8000;

export function readRecentItems(): RecentItem[] {
    try {
        const raw = localStorage.getItem(RECENT_STORAGE_KEY);
        const items = raw ? JSON.parse(raw) : [];
        return Array.isArray(items) ? items : [];
    } catch {
        return [];
    }
}

// A review page visiting itself writes to "recently viewed" on load,
// independent of whether search is ever opened this visit — the shell just
// reads whatever's accumulated so far.
export function recordRecentlyViewed() {
    const heroTitle = document.querySelector<HTMLElement>("article[data-pagefind-body] .hero-info h1");
    const title = heroTitle?.textContent?.trim();
    if (!title) {
        return;
    }

    const url = window.location.pathname;
    const items = readRecentItems().filter((item) => item.url !== url);
    items.unshift({ title, url });

    try {
        localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(items.slice(0, RECENT_MAX)));
    } catch {
        // localStorage unavailable (private browsing, quota) — recently
        // viewed just won't persist this session, nothing else depends on it.
    }
}

export function renderRecentlyViewed(recentList: HTMLElement | null) {
    if (!recentList) {
        return;
    }

    // Excludes the current page, same convention as the sidebar's own
    // "Latest" module — visiting a review shouldn't list itself.
    const items = readRecentItems().filter((item) => item.url !== window.location.pathname);

    if (!items.length) {
        recentList.innerHTML = '<div class="search-recent-empty">No recently viewed reviews yet.</div>';
        return;
    }

    recentList.innerHTML = "";
    items.forEach((item) => {
        const a = document.createElement("a");
        a.className = "search-recent-item";
        a.href = item.url;
        a.textContent = item.title;
        recentList.appendChild(a);
    });
}

// Pagefind's filter-pane component doesn't document an attribute for
// pre-selecting a value on load, so this drives it the same way a user
// would: find the rendered checkbox whose label matches the current locale
// and click it for real. Runs once per shell (guarded via onSeeded),
// retried via MutationObserver since the filter-pane's options only
// populate once Pagefind's index bundle finishes loading.
export function seedLocaleFilter(shell: HTMLElement, localeName: string | undefined, onSeeded: () => void) {
    if (!localeName) {
        onSeeded();
        return;
    }

    const pane = shell.querySelector<HTMLElement>(".search-filter-pane");
    if (!pane) {
        onSeeded();
        return;
    }

    // Scoped to input[name="locale"] specifically (not every checkbox in
    // the pane) and matched by exact value (not a label-text prefix match)
    // — a prefix match on "Hawaii" would match the Cuisine option
    // "Hawaiian" before it ever found the real (hidden) Locale option
    // "Hawaii". Every checkbox's real value attribute holds the exact
    // facet value Pagefind indexed, so an exact compare against that —
    // scoped to the one group that can't collide with another facet's
    // values — can't misfire the way a fuzzy text match across the whole
    // pane could.
    const tryClick = (): boolean => {
        const checkboxes = Array.from(pane.querySelectorAll<HTMLInputElement>('input[name="locale"]'));
        const match = checkboxes.find((cb) => cb.value.trim().toLowerCase() === localeName.toLowerCase());

        if (!match) {
            return false;
        }

        if (!match.checked) {
            match.click();
        }

        return true;
    };

    let seeded = false;
    const markSeeded = () => {
        if (!seeded) {
            seeded = true;
            onSeeded();
        }
    };

    // Doesn't disconnect on the first successful click: Pagefind can
    // re-render the filter pane's checkboxes from scratch shortly after
    // (e.g. while a debounced query is still settling, or the index is
    // still warming up), discarding our click along with the rest of that
    // render. Confirmed live — the very first render pass after page load
    // can carry the right checkbox already but still get replaced a moment
    // later, most visible on the /search/ page (search-page.ts) since
    // seeding starts on page load rather than on focus (search.ts), i.e.
    // closer to that warm-up window. Re-asserting on every subsequent
    // mutation makes this self-healing regardless of how many render
    // passes happen — the timeout below is just a backstop for the locale
    // genuinely not existing as a facet value at all.
    tryClick();
    const observer = new MutationObserver(() => {
        if (tryClick()) {
            markSeeded();
        }
    });
    observer.observe(pane, { childList: true, subtree: true });

    window.setTimeout(() => {
        observer.disconnect();
        markSeeded();
    }, LOCALE_SEED_TIMEOUT_MS);
}

// Real checkbox clicks (rather than any undocumented internal API) so this
// keeps working regardless of how pagefind-filter-pane wires its own state
// internally.
export function clearFilters(shell: HTMLElement, isScoped: boolean) {
    const pane = shell.querySelector(".search-filter-pane");

    pane?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach((cb) => {
        // On a scoped page the seeded Locale checkbox is hidden (see
        // search.scss) and isn't a user-added filter — clearing it would
        // silently broaden results to every locale with no visible control
        // left to undo that.
        if (isScoped && cb.name === "locale") {
            return;
        }

        cb.checked = false;
        cb.dispatchEvent(new Event("change", { bubbles: true }));
    });
}

// Below 700px the filter pane stacks above the results, and with every
// Area/Cuisine option listed it ran ~1,900px tall — the first result sat
// about three screens down. There it's collapsed behind this "Filters"
// toggle instead (search.scss hides the pane unless the shell carries
// .filters-open), so results come first. The label carries the number of
// filters the user has added, so a collapsed pane still says it's active.
// Above 700px the toggle is hidden and the pane always shows.
export function bindFiltersToggle(shell: HTMLElement) {
    const toggle = shell.querySelector<HTMLButtonElement>(".search-filters-toggle");
    const pane = shell.querySelector<HTMLElement>(".search-filter-pane");
    if (!toggle || !pane) {
        return;
    }

    toggle.addEventListener("click", () => {
        const open = shell.classList.toggle("filters-open");
        toggle.setAttribute("aria-expanded", String(open));
    });

    const syncLabel = () => {
        // The seeded Locale checkbox on a scoped page isn't a user-added
        // filter (same rule as clearFilters()/search.scss).
        const isScoped = !!shell.dataset.seedLocale;
        const checked = Array.from(pane.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked')).filter(
            (cb) => !(isScoped && cb.name === "locale"),
        ).length;
        toggle.textContent = checked ? `Filters (${checked})` : "Filters";
    };

    pane.addEventListener("change", syncLabel);
    new MutationObserver(syncLabel).observe(pane, { childList: true, subtree: true });
}
